import {
  WebFilterPlacementKind,
  WebFilterPlacementSection,
  type GetWebFilterPlacementOrderResponse,
  type WebFilterPlacementLite,
} from '@/api/generated/schemas';

export interface PlacementOrderRow {
  id: string | null;
  webFilterId: string | null;
  filterName: string;
  order: number;
  isCurrentFilter: boolean;
}

export interface PlacementOrderGroup {
  kind: WebFilterPlacementKind;
  items: PlacementOrderRow[];
}

export interface PlacementOrderEditorState {
  groups: PlacementOrderGroup[];
  dirtyKinds: WebFilterPlacementKind[];
}

const KIND_SORT_ORDER: WebFilterPlacementKind[] = [
  WebFilterPlacementKind.MenuBanner,
  WebFilterPlacementKind.MenuTile,
  WebFilterPlacementKind.MenuSpecial,
  WebFilterPlacementKind.MenuLink,
  WebFilterPlacementKind.SearchSpecialPeriods,
  WebFilterPlacementKind.SearchTourTypes,
  WebFilterPlacementKind.HomeFindTour,
];

export function emptyPlacementOrderState(): PlacementOrderEditorState {
  return { groups: [], dirtyKinds: [] };
}

export function fromPlacementOrderResponse(
  response: GetWebFilterPlacementOrderResponse,
  filterName: string,
  isCreate: boolean
): PlacementOrderEditorState {
  return {
    groups: response.groups
      .map((group) => ({
        kind: group.kind,
        items: group.items.map((item) => ({
          id: item.isCurrentFilter && isCreate ? null : item.id,
          webFilterId: item.webFilterId,
          filterName: item.isCurrentFilter ? filterName : item.filterName,
          order: item.order,
          isCurrentFilter: item.isCurrentFilter,
        })),
      }))
      .sort((a, b) => kindSortIndex(a.kind) - kindSortIndex(b.kind)),
    dirtyKinds: [],
  };
}

function kindSortIndex(kind: WebFilterPlacementKind): number {
  const index = KIND_SORT_ORDER.indexOf(kind);
  return index >= 0 ? index : Number.MAX_SAFE_INTEGER;
}

function renumberItems(items: PlacementOrderRow[]): PlacementOrderRow[] {
  return items.map((item, index) => ({ ...item, order: index + 1 }));
}

function sortGroups(groups: PlacementOrderGroup[]): PlacementOrderGroup[] {
  return [...groups].sort((a, b) => kindSortIndex(a.kind) - kindSortIndex(b.kind));
}

export function applyEnableKind(
  state: PlacementOrderEditorState,
  kind: WebFilterPlacementKind,
  filterName: string,
  webFilterId: string | null
): PlacementOrderEditorState {
  const groupIndex = state.groups.findIndex((group) => group.kind === kind);
  if (groupIndex >= 0) {
    const group = state.groups[groupIndex]!;
    if (group.items.some((item) => item.isCurrentFilter)) {
      return {
        ...state,
        groups: state.groups.map((g, index) =>
          index === groupIndex
            ? {
                ...g,
                items: g.items.map((item) =>
                  item.isCurrentFilter ? { ...item, filterName, webFilterId } : item
                ),
              }
            : g
        ),
      };
    }

    const newItems = renumberItems([
      ...group.items,
      {
        id: null,
        webFilterId,
        filterName,
        order: group.items.length + 1,
        isCurrentFilter: true,
      },
    ]);
    return {
      ...state,
      groups: state.groups.map((g, index) =>
        index === groupIndex ? { ...g, items: newItems } : g
      ),
    };
  }

  return {
    ...state,
    groups: sortGroups([
      ...state.groups,
      {
        kind,
        items: [
          {
            id: null,
            webFilterId,
            filterName,
            order: 1,
            isCurrentFilter: true,
          },
        ],
      },
    ]),
  };
}

export function applyDisableKind(
  state: PlacementOrderEditorState,
  kind: WebFilterPlacementKind
): PlacementOrderEditorState {
  return {
    ...state,
    groups: state.groups
      .map((group) =>
        group.kind === kind
          ? {
              ...group,
              items: renumberItems(group.items.filter((item) => !item.isCurrentFilter)),
            }
          : group
      )
      .filter((group) => group.items.length > 0),
    dirtyKinds: state.dirtyKinds.filter((dirtyKind) => dirtyKind !== kind),
  };
}

export function reorderInGroup(
  state: PlacementOrderEditorState,
  kind: WebFilterPlacementKind,
  fromIndex: number,
  toIndex: number
): PlacementOrderEditorState {
  if (fromIndex === toIndex) {
    return state;
  }

  const groupIndex = state.groups.findIndex((group) => group.kind === kind);
  if (groupIndex < 0) {
    return state;
  }

  const group = state.groups[groupIndex]!;
  const items = [...group.items];
  const [moved] = items.splice(fromIndex, 1);
  if (!moved) {
    return state;
  }
  items.splice(toIndex, 0, moved);

  const dirtyKinds = state.dirtyKinds.includes(kind)
    ? state.dirtyKinds
    : [...state.dirtyKinds, kind];

  return {
    ...state,
    dirtyKinds,
    groups: state.groups.map((g, index) =>
      index === groupIndex ? { ...g, items: renumberItems(items) } : g
    ),
  };
}

export function updateCurrentFilterName(
  state: PlacementOrderEditorState,
  filterName: string,
  webFilterId: string | null
): PlacementOrderEditorState {
  return {
    ...state,
    groups: state.groups.map((group) => ({
      ...group,
      items: group.items.map((item) =>
        item.isCurrentFilter ? { ...item, filterName, webFilterId } : item
      ),
    })),
  };
}

export function toPlacementOrderWriteLite(
  state: PlacementOrderEditorState
): WebFilterPlacementLite[] {
  const result: WebFilterPlacementLite[] = [];
  for (const kind of state.dirtyKinds) {
    const group = state.groups.find((g) => g.kind === kind);
    if (!group) {
      continue;
    }
    for (const item of group.items) {
      result.push({
        id: item.isCurrentFilter ? null : item.id,
        kind,
        order: item.order,
      });
    }
  }
  return result;
}

export function visibleGroupsForEnabledKinds(
  state: PlacementOrderEditorState,
  enabledKinds: WebFilterPlacementKind[]
): PlacementOrderGroup[] {
  const enabledSet = new Set(enabledKinds);
  return state.groups.filter((group) => enabledSet.has(group.kind));
}

export function resolveEnabledKinds(
  placements: {
    kind: WebFilterPlacementKind;
    isEnabled: boolean;
    section: WebFilterPlacementSection;
  }[],
  sectionMenuEnabled: boolean,
  sectionSearchEnabled: boolean,
  sectionHpEnabled: boolean
): WebFilterPlacementKind[] {
  return placements
    .filter((placement) => {
      if (!placement.isEnabled) {
        return false;
      }
      if (placement.section === WebFilterPlacementSection.MainMenu && !sectionMenuEnabled) {
        return false;
      }
      if (placement.section === WebFilterPlacementSection.SearchFilter && !sectionSearchEnabled) {
        return false;
      }
      if (placement.section === WebFilterPlacementSection.HomePage && !sectionHpEnabled) {
        return false;
      }
      return true;
    })
    .map((placement) => placement.kind);
}
